// DestinationPhotoGallery — masonry-ish photo grid with a click-to-zoom lightbox.
// Falls back gracefully when there are no photos.
import React, { useState, useEffect, useCallback } from 'react';
import { B } from '../constants';

export default function DestinationPhotoGallery({ photos }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const close = useCallback(() => setLightboxIndex(null), []);
  const next = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i + 1) % photos.length));
  }, [photos.length]);
  const prev = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i - 1 + photos.length) % photos.length));
  }, [photos.length]);

  useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, close, next, prev]);

  if (!photos || photos.length === 0) return null;

  return (
    <section style={{ margin: '24px 0' }}>
      <h2 style={{
        fontFamily: "'Playfair Display',serif", color: B.night, fontSize: 24, marginBottom: 12,
      }}>
        Photos
      </h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: 8,
      }}>
        {photos.map((p, idx) => (
          <button
            key={p.id ?? idx}
            type="button"
            onClick={() => setLightboxIndex(idx)}
            style={{
              padding: 0, border: 'none', cursor: 'pointer', borderRadius: 8, overflow: 'hidden',
              background: B.sand, aspectRatio: '4 / 3',
            }}
            aria-label={p.caption || `Photo ${idx + 1}`}
          >
            <img
              src={p.url}
              alt={p.caption || ''}
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </button>
        ))}
      </div>

      {lightboxIndex !== null && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={close}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous photo"
            style={{
              position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none',
              borderRadius: '50%', width: 44, height: 44, fontSize: 24, cursor: 'pointer',
            }}
          >‹</button>
          <figure onClick={(e) => e.stopPropagation()} style={{ margin: 0, maxWidth: '90vw', maxHeight: '90vh' }}>
            <img
              src={photos[lightboxIndex].url}
              alt={photos[lightboxIndex].caption || ''}
              style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', display: 'block' }}
            />
            {(photos[lightboxIndex].caption || photos[lightboxIndex].credit) && (
              <figcaption style={{ color: '#fff', textAlign: 'center', marginTop: 12, fontSize: 14 }}>
                {photos[lightboxIndex].caption}
                {photos[lightboxIndex].credit && (
                  <span style={{ marginLeft: 8, opacity: 0.7 }}>© {photos[lightboxIndex].credit}</span>
                )}
              </figcaption>
            )}
          </figure>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next photo"
            style={{
              position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none',
              borderRadius: '50%', width: 44, height: 44, fontSize: 24, cursor: 'pointer',
            }}
          >›</button>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            style={{
              position: 'absolute', top: 16, right: 16,
              background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none',
              borderRadius: '50%', width: 36, height: 36, fontSize: 18, cursor: 'pointer',
            }}
          >×</button>
        </div>
      )}
    </section>
  );
}
